import { ArrowLeft, PanelLeft } from "lucide-react";
import React from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { SidebarContext } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: string;
  description?: string;
  children?: React.ReactNode;
  className?: string;
  backTo?: string;
  onBack?: () => void;
  icon?: React.ReactNode;
  titleExtra?: React.ReactNode;
  titleTestId?: string;
}

export function PageHeader({
  title,
  description,
  children,
  className,
  backTo,
  onBack,
  icon,
  titleExtra,
  titleTestId,
}: PageHeaderProps) {
  const sidebarCtx = React.useContext(SidebarContext);
  const [, setLocation] = useLocation();

  const handleBack = backTo
    ? () => {
        if (window.history.length > 1) {
          window.history.back();
        } else {
          setLocation(backTo);
        }
      }
    : onBack;

  const backButton =
    backTo || onBack ? (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        data-testid="button-back"
        onClick={handleBack}
      >
        <ArrowLeft className="w-4 h-4" />
      </Button>
    ) : null;

  return (
    <div
      className={cn(
        "flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-4",
        className,
      )}
    >
      <div className="flex items-center gap-1">
        {sidebarCtx && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={sidebarCtx.toggleSidebar}
            data-testid="button-sidebar-toggle-page"
          >
            <PanelLeft className="w-4 h-4" />
          </Button>
        )}
        {backButton}
        {icon && <span className="shrink-0">{icon}</span>}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1
              className="text-base font-semibold tracking-tight truncate"
              data-testid={titleTestId}
            >
              {title}
            </h1>
            {titleExtra}
          </div>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
      {children && <div className="flex items-center gap-2 mt-4 sm:mt-0">{children}</div>}
    </div>
  );
}
